import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, LogOut, User, Calendar, ExternalLink, BookOpen, Menu, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const isTestPage = location.pathname === '/test';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-sm relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            {!isTestPage && (
              <Link to="/" className="flex items-center">
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/5b8d80bc50a54f3097036fbc/1546638478704-ITJL00DVVX93GXFZ4FY1/logo_transparent_background.png?format=750w" 
                  alt="Maximum Effectiveness Logo" 
                  className="h-8 sm:h-10"
                />
              </Link>
            )}
            {isTestPage && (
              <img 
                src="https://images.squarespace-cdn.com/content/v1/5b8d80bc50a54f3097036fbc/1546638478704-ITJL00DVVX93GXFZ4FY1/logo_transparent_background.png?format=750w" 
                alt="Maximum Effectiveness Logo" 
                className="h-8 sm:h-10"
              />
            )}
          </div>

          {/* Desktop Navigation */}
          {!isTestPage && (
            <div className="hidden sm:flex items-center space-x-4">
              <a
                href="https://www.maximumeffectiveness.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Website</span>
              </a>
              
              <a
                href="https://www.maximumeffectiveness.com/book-online"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sm font-medium bg-[#2B5AB8] text-white hover:bg-[#224A96] px-3 py-2 rounded-md transition-colors"
              >
                <Calendar className="h-4 w-4" />
                <span>Book Appointment</span>
              </a>
              
              {isAuthenticated ? (
                <>
                  {user?.role === 'admin' ? (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
                    >
                      <User className="h-4 w-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  ) : (
                    <Link
                      to="/dashboard"
                      className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
                    >
                      <User className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Sign up</span>
                  </Link>
                  <Link
                    to="/login"
                    className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
                  >
                    <LogIn className="h-4 w-4" />
                    <span>Login</span>
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Mobile Navigation Button & Test Page Actions */}
          <div className="flex items-center space-x-2">
            {isTestPage && (
              <a
                href="https://www.maximumeffectiveness.com/book-online"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-xs sm:text-sm font-medium bg-[#2B5AB8] text-white hover:bg-[#224A96] px-2 py-1.5 sm:px-3 sm:py-2 rounded-md transition-colors"
              >
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Book</span>
                <span className="xs:hidden">📅</span>
              </a>
            )}
            
            {!isTestPage && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="sm:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 hover:bg-gray-100 transition-colors"
                aria-expanded="false"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {!isTestPage && isMobileMenuOpen && (
          <div className="sm:hidden absolute top-16 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
            <div className="px-4 py-6 space-y-4">
              <a
                href="https://www.maximumeffectiveness.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="h-5 w-5 text-gray-400" />
                <span className="font-medium">Website</span>
              </a>
              
              <a
                href="https://www.maximumeffectiveness.com/book-online"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 p-3 rounded-lg bg-[#2B5AB8] text-white hover:bg-[#224A96] transition-colors"
              >
                <Calendar className="h-5 w-5" />
                <span className="font-medium">Book Appointment</span>
              </a>
              
              <div className="border-t border-gray-200 pt-4">
                {isAuthenticated ? (
                  <>
                    {user?.role === 'admin' ? (
                      <Link
                        to="/admin"
                        onClick={closeMobileMenu}
                        className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <User className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">Admin Dashboard</span>
                      </Link>
                    ) : (
                      <Link
                        to="/dashboard"
                        onClick={closeMobileMenu}
                        className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <User className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">Dashboard</span>
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/signup"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <BookOpen className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">Sign up</span>
                    </Link>
                    <Link
                      to="/login"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogIn className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">Login</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;